<script>
// Inline SVG icon set for the rich student Home and the student shell (P3,
// student-foundation-redesign). Same discipline as StudentProfile/StudentIcon
// .vue -- a single functional component over a small fixed shape set -- but
// scoped to this initiative rather than reaching across scope boundaries.
//
// Shapes are copied byte-for-byte from the rendered canonical prototype's
// own `data-icon` markup (00_*.md §13.1, SHA-256
// 6b1ff8b5031aee5b48fc3c71ca7ad303a098cfe62207e8584979d003c3df5dff), not
// approximated -- an earlier pass here used hand-drawn look-alikes for
// several of these (teacher, spark, book, chat, user, headset, arrow) and
// none of them were the prototype's actual glyph.
const SHAPES = {
  spark: [
    {
      tag: 'path',
      d: 'm12 2 1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6Z',
    },
    {
      tag: 'path',
      d: 'm19 16 .8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8Z',
    },
  ],
  arrow: [
    { tag: 'path', d: 'M19 12H5' },
    { tag: 'path', d: 'm11 6-6 6 6 6' },
  ],
  // Student Journey V2 setup: the back-link arrow (the mirror of `arrow`) and
  // the profile-row pencil, both taken from the reference's own `data-icon`
  // markup.
  back: [
    { tag: 'path', d: 'M5 12h14' },
    { tag: 'path', d: 'm13 6 6 6-6 6' },
  ],
  edit: [
    { tag: 'path', d: 'M12 20h9' },
    { tag: 'path', d: 'M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z' },
  ],
  chat: [
    {
      tag: 'path',
      d: 'M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z',
    },
    { tag: 'path', d: 'M8 9h8M8 13h5' },
  ],
  check: [{ tag: 'path', d: 'm5 12 4 4L19 6' }],
  book: [
    {
      tag: 'path',
      d: 'M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23Z',
    },
    {
      tag: 'path',
      d: 'M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5a3.5 3.5 0 0 1 3.5 3Z',
    },
  ],
  teacher: [
    { tag: 'circle', cx: 9, cy: 8, r: 4 },
    { tag: 'path', d: 'M2 21a7 7 0 0 1 14 0M17 8h5M19.5 5.5v5' },
  ],
  headset: [
    { tag: 'path', d: 'M4 14v-2a8 8 0 0 1 16 0v2' },
    {
      tag: 'path',
      d:
        'M4 14h3v6H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 1-2ZM20 14h-3v6h2a2 2 0 0 0 2-2v-2a2 2 0 0 0-1-2ZM17 20c-1 2-3 2-5 2',
    },
  ],
  user: [
    { tag: 'circle', cx: 12, cy: 8, r: 4 },
    { tag: 'path', d: 'M4.5 21a7.5 7.5 0 0 1 15 0' },
  ],
  // WP-03-CHAT-VISUAL-PARITY (doc 11): the four glyphs the rendered
  // prototype's chat composer/context bar use, copied from its own
  // `data-icon` markup (camera, mic, send, more).
  camera: [
    { tag: 'rect', x: 3, y: 6, width: 18, height: 14, rx: 3 },
    { tag: 'path', d: 'm8 6 1.5-2h5L16 6' },
    { tag: 'circle', cx: 12, cy: 13, r: 4 },
  ],
  // STUDENT CHAT V2 image flow: the V2 reference's own `close` glyph (image
  // preview remove, image-source menu close).
  close: [{ tag: 'path', d: 'm6 6 12 12M18 6 6 18' }],
  mic: [
    { tag: 'rect', x: 9, y: 3, width: 6, height: 12, rx: 3 },
    { tag: 'path', d: 'M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6' },
  ],
  send: [
    { tag: 'path', d: 'm21 3-18 9 18 9-4-9Z' },
    { tag: 'path', d: 'M17 12H3' },
  ],
  more: [
    { tag: 'circle', cx: 5, cy: 12, r: 1, fill: 'currentColor' },
    { tag: 'circle', cx: 12, cy: 12, r: 1, fill: 'currentColor' },
    { tag: 'circle', cx: 19, cy: 12, r: 1, fill: 'currentColor' },
  ],
  // WP-04V-C. Both copied from the CURRENT pinned prototype's own
  // `data-icon` markup (sha256:75678bee...7bba), read out of the rendered
  // sidebar rather than approximated.
  //
  // `logout` in particular was already being asked for by
  // SidebarLinks.vue's student logout row, but no shape existed under that
  // name -- `render()` returns null for an unknown name, so that row has been
  // rendering with no icon at all.
  language: [
    { tag: 'circle', cx: 12, cy: 12, r: 9 },
    {
      tag: 'path',
      d: 'M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
    },
  ],
  logout: [{ tag: 'path', d: 'M10 5H5v14h5M14 8l4 4-4 4M18 12H9' }],
  // V405 (doc 26). Copied from the rendered V405 reference's own markup
  // (sha256 ea21cc7b...9cfd): the Reports toolbar's filter and chart glyphs,
  // and the chat start guide's "write" and "image" glyphs.
  filter: [{ tag: 'path', d: 'M4 6h16M7 12h10M10 18h4' }],
  chart: [{ tag: 'path', d: 'M4 20V10M10 20V4M16 20v-7M22 20H2' }],
  compose: [
    { tag: 'path', d: 'M5 6h14M5 12h9M5 18h6' },
    { tag: 'path', d: 'M18 14v6M15 17h6' },
  ],
  // V405-F1: the reference's «سؤال یا یادگیری جدید» glyph.
  plus: [{ tag: 'path', d: 'M12 5v14M5 12h14' }],
  // CR-03 (doc 28 §17): the reference's `coin` -- the AI credit pill and the
  // zero-credit warning (BORNA_final405-07-06.html, data-icon="coin").
  coin: [
    { tag: 'circle', cx: 12, cy: 12, r: 8.5 },
    { tag: 'circle', cx: 12, cy: 12, r: 5.5 },
    {
      tag: 'path',
      d:
        'M12 8.8v6.4M9.8 10.2h3.3a1.35 1.35 0 0 1 0 2.7h-2.2a1.35 1.35 0 0 0 0 2.7h3.3',
    },
  ],
  image: [
    { tag: 'rect', x: 3, y: 4, width: 18, height: 16, rx: 2 },
    { tag: 'circle', cx: 9, cy: 10, r: 2 },
    { tag: 'path', d: 'm21 15-5-5L5 20' },
  ],
  // Package 38 UX-3: the V405 chat start guide's whiteboard glyph
  // (BORNA_final405-07-06.html, `.borna-v12-guide-icon`, third item).
  board: [
    { tag: 'rect', x: 3, y: 4, width: 18, height: 13, rx: 2 },
    { tag: 'path', d: 'M8 20h8M12 17v3' },
    { tag: 'path', d: 'M7 8h7' },
  ],
}

function shapeAttrs(shape) {
  switch (shape.tag) {
    case 'circle':
      return { cx: shape.cx, cy: shape.cy, r: shape.r, fill: shape.fill }
    case 'rect':
      return {
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
        rx: shape.rx,
      }
    default:
      return { d: shape.d }
  }
}

export default {
  name: 'StudentHomeIcon',
  functional: true,
  props: {
    name: { type: String, required: true },
    size: { type: Number, default: 20 },
  },
  render(h, { props }) {
    const shapes = SHAPES[props.name]
    if (!shapes) return null
    return h(
      'svg',
      {
        attrs: {
          width: props.size,
          height: props.size,
          viewBox: '0 0 24 24',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 1.8,
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'aria-hidden': 'true',
          focusable: 'false',
        },
      },
      shapes.map(shape => h(shape.tag, { attrs: shapeAttrs(shape) }))
    )
  },
}
</script>
